import { BROKER_LINKS } from "@/data/brokers";
import { useState, type MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { flashTarget } from '@/hooks/flashTarget';

interface MegaItem {
  label: string;
  to: string;
  tab?: string;
  anchor?: string;
}

interface MegaColumn {
  heading?: string;
  to: string;
  items: MegaItem[];
}

interface NavEntry {
  label: string;
  to?: string;
  mega?: {
    columns: MegaColumn[];
  };
}

function megaHref(item: MegaItem): string {
  if (item.tab) return `${item.to}?tab=${item.tab}`;
  if (item.anchor) return `${item.to}#${item.anchor}`;
  return item.to;
}

const NAV_ITEMS: NavEntry[] = [
  { label: 'Trang chủ', to: '/' },
  { label: 'Máy tính Khối lượng', to: '/may-tinh-khoi-luong' },
  { label: 'Máy tính Ký quỹ', to: '/may-tinh-ky-quy' },
  {
    label: 'Kho Công cụ',
    mega: {
      columns: [
        {
          heading: 'Tính toán Lợi nhuận',
          to: '/tinh-toan-loi-nhuan',
          items: [
            { label: 'Tính toán lợi nhuận', to: '/tinh-toan-loi-nhuan', tab: 'profit' },
            { label: 'Tính chi phí Spread', to: '/tinh-toan-loi-nhuan', tab: 'spread' },
            { label: 'Máy tính lợi nhuận kép', to: '/tinh-toan-loi-nhuan', tab: 'compound' },
          ],
        },
        {
          heading: 'Quản trị Rủi ro',
          to: '/quan-tri-rui-ro',
          items: [
            { label: 'Máy tính rủi ro', to: '/quan-tri-rui-ro', tab: 'risk' },
            { label: 'Máy tính Drawdown', to: '/quan-tri-rui-ro', tab: 'drawdown' },
            { label: 'Rủi ro phá sản', to: '/quan-tri-rui-ro', tab: 'ruin' },
          ],
        },
      ],
    },
  },
  {
    label: 'Phân tích Giao dịch',
    mega: {
      columns: [
        {
          heading: 'Phân tích Kỹ thuật',
          to: '/phan-tich-ky-thuat',
          items: [
            { label: 'Máy tính Pivot Point', to: '/phan-tich-ky-thuat', anchor: 'pivot' },
            { label: 'Máy tính Fibonacci', to: '/phan-tich-ky-thuat', anchor: 'fibonacci' },
            { label: 'Phân tích dữ liệu chiến lược', to: '/phan-tich-ky-thuat', anchor: 'strategy' },
          ],
        },
        {
          heading: 'Đối chiếu Thị trường',
          to: '/doi-chieu-thi-truong',
          items: [
            { label: 'So sánh Spread Forex/CFD', to: '/doi-chieu-thi-truong', anchor: 'spread' },
            { label: 'So sánh Swap Forex/CFD', to: '/doi-chieu-thi-truong', anchor: 'swap' },
            { label: 'Kiểm tra thông số sản phẩm', to: '/doi-chieu-thi-truong', anchor: 'product' },
          ],
        },
      ],
    },
  },
  { label: 'Lịch Thị trường', to: '/lich-thi-truong' },
];

export default function AppHeader() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMega, setExpandedMega] = useState<string | null>(null);

  /**
   * When the user clicks an anchor item that points to the SAME page they are
   * already on, React Router would navigate (hash-only) but that path is
   * fragile. Handle it directly: scroll to the section and keep the URL hash
   * in sync without a full navigation.
   */
  const handleMegaClick =
    (sub: MegaItem) => (e: MouseEvent<HTMLAnchorElement>) => {
      if (!sub.anchor) return;
      if (location.pathname !== sub.to) return;
      const el = document.getElementById(sub.anchor);
      if (!el) return;
      e.preventDefault();
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      flashTarget(el);
      window.history.replaceState(null, '', `${window.location.pathname}#${sub.anchor}`);
      closeMenu();
    };

  const isMegaActive = (item: NavEntry): boolean => {
    if (!item.mega) return false;
    return item.mega.columns.some(
      (col) =>
        col.to === location.pathname ||
        col.items.some((sub) => sub.to === location.pathname),
    );
  };

  const closeMenu = () => {
    setMobileOpen(false);
    setExpandedMega(null);
  };

  return (
    <header className="fixed top-4 left-0 right-0 z-50 px-4 md:px-6">
      <div className="mx-auto flex h-14 max-w-[1180px] items-center justify-between rounded-full bg-white border border-slate-200/80 px-3 md:px-4 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        {/* Brand */}
        <Link to="/" className="flex items-center whitespace-nowrap pl-2 pr-4">
          <img src="/images/logo.png" alt="ForexTools" className="h-14 object-contain" />
        </Link>

        {/* Nav links (desktop) */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV_ITEMS.map((item) =>
            item.mega ? (
              <div key={item.label} className="group relative">
                <button
                  className={`flex items-center whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                    isMegaActive(item)
                      ? 'bg-violet-50 text-violet-700'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {item.label}
                  <span className="ml-1 text-[9px] text-slate-400">▾</span>
                </button>
                {/* Mega panel */}
                <div className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0 translate-y-1">
                  <div className="flex gap-8 rounded-2xl frosted-strong border border-white/70 p-6 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.18)]">
                    {item.mega.columns.map((col) => (
                      <div key={col.heading ?? col.to} className="min-w-[200px]">
                        {col.heading ? (
                          <Link
                            to={col.to}
                            className="mb-3 flex items-center justify-between whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-violet-600"
                          >
                            {col.heading}
                            <i className="ri-arrow-right-up-line text-sm"></i>
                          </Link>
                        ) : null}
                        <ul className="space-y-1">
                          {col.items.map((sub) => (
                            <li key={sub.label}>
                              <Link
                                to={megaHref(sub)}
                                onClick={handleMegaClick(sub)}
                                className="block whitespace-nowrap rounded-lg px-2 py-1.5 text-sm text-slate-600 transition-colors hover:bg-violet-50 hover:text-violet-700"
                              >
                                {sub.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to ?? '/'}
                className={`whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname === (item.to ?? '/')
                    ? 'bg-violet-50 text-violet-700'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {item.label}
              </Link>
            ),
          )}
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-2">
          <a
            href={BROKER_LINKS['XM']}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex whitespace-nowrap rounded-full bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
          >
            Mở Tài khoản
          </a>
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Mở menu"
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full text-slate-700 hover:bg-slate-100"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="mx-auto mt-2 max-w-[1180px] lg:hidden">
          <div className="max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl bg-white border border-slate-200/80 p-2 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.18)]">
            <nav className="space-y-1">
              {NAV_ITEMS.map((item) =>
                item.mega ? (
                  <div key={item.label} className="rounded-xl">
                    <button
                      onClick={() =>
                        setExpandedMega((v) => (v === item.label ? null : item.label))
                      }
                      className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                    >
                      <span className={isMegaActive(item) ? 'text-violet-700' : ''}>
                        {item.label}
                      </span>
                      <i
                        className={`ri-arrow-down-s-line text-base text-slate-400 transition-transform ${
                          expandedMega === item.label ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {expandedMega === item.label && (
                      <div className="px-2 pb-2">
                        {item.mega.columns.map((col) => (
                          <div key={col.heading ?? col.to} className="mb-1">
                            {col.heading && (
                              <Link
                                to={col.to}
                                onClick={closeMenu}
                                className="flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-violet-600 hover:bg-violet-50 rounded-lg"
                              >
                                {col.heading}
                                <i className="ri-arrow-right-up-line text-sm"></i>
                              </Link>
                            )}
                            <ul>
                              {col.items.map((sub) => (
                                <li key={sub.label}>
                                  <Link
                                    to={megaHref(sub)}
                                    onClick={(e) => {
                                      handleMegaClick(sub)(e);
                                      closeMenu();
                                    }}
                                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-violet-50 hover:text-violet-700"
                                  >
                                    <i className="ri-corner-down-right-line text-xs text-slate-300"></i>
                                    {sub.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.to ?? '/'}
                    onClick={closeMenu}
                    className={`flex items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                      location.pathname === (item.to ?? '/')
                        ? 'bg-violet-50 text-violet-700'
                        : 'text-slate-800 hover:bg-slate-50'
                    }`}
                  >
                    {item.label}
                    <i className="ri-arrow-right-s-line text-base text-slate-300"></i>
                  </Link>
                ),
              )}
            </nav>
            <a
              href={BROKER_LINKS['XM']}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
              className="mt-2 flex items-center justify-center whitespace-nowrap rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-violet-700 sm:hidden"
            >
              Mở Tài khoản
            </a>
          </div>
        </div>
      )}
    </header>
  );
}